import React from 'react';
import {connect} from 'react-redux';

import * as FileConstants from 'folder-items/FileConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {requestFetchManagedModel} from 'models/ManagedModelUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import pick from 'util/pick';
import {isContainable} from 'workflows/util/StageRunSampleUtil';
import {StageRunSample, StageRunSampleDetails} from 'workflows/WorkflowConstants';

type BioentitySummary = FileConstants.BioentitySummary;

interface SampleDetailsByTableName {
  [tableName: string]: Array<StageRunSampleDetails>;
}

function getUniqueEntityIds(sampleDetailsByTableName: SampleDetailsByTableName): Array<string> {
  const entityIds = new Set<string>();
  for (const sampleDetails of Object.values(sampleDetailsByTableName)) {
    for (const {stageRunSample} of sampleDetails) {
      if (stageRunSample.fileId != null) {
        entityIds.add(stageRunSample.fileId);
      }
    }
  }
  return [...entityIds];
}

export function mapStateToProps(
  state: BenchlingState,
  {sampleDetailsByTableName}: {sampleDetailsByTableName: SampleDetailsByTableName}
): {
  fileSummariesById: {[id: string]: BioentitySummary};
  isLoading: boolean;
} {
  const entityIds = getUniqueEntityIds(sampleDetailsByTableName);
  const fileSummariesById = pick(state.models.fileSummariesById, ...entityIds);
  const isLoading = entityIds.some((entityId) => fileSummariesById[entityId] == null);
  return {fileSummariesById, isLoading};
}

export interface TransferTableNonContainableEntityFilterProps {
  children: (
    filteredSampleDetailsByTableName: SampleDetailsByTableName,
    isLoading: boolean
  ) => JSX.Element | null;
  loadingPlaceholder?: JSX.Element;
  sampleDetailsByTableName: SampleDetailsByTableName;
  // mapStateToProps
  fileSummariesById: {[id: string]: BioentitySummary};
  isLoading: boolean;
}

/**
 * Filter out non-containable samples from a sampleDetailsByTableName object.
 *
 * Deleted samples are not filtered out.
 */
export class TransferTableNonContainableEntityFilter extends React.PureComponent<TransferTableNonContainableEntityFilterProps> {
  override componentDidMount(): void {
    for (const entityId of getUniqueEntityIds(this.props.sampleDetailsByTableName)) {
      requestFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, entityId);
    }
  }

  override componentDidUpdate(): void {
    for (const entityId of getUniqueEntityIds(this.props.sampleDetailsByTableName)) {
      requestFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, entityId);
    }
  }

  _isSampleContainable(stageRunSample: StageRunSample): boolean {
    if (!stageRunSample.fileId) {
      return true;
    }
    return isContainable(stageRunSample, this.props.fileSummariesById[stageRunSample.fileId]);
  }

  override render(): JSX.Element | null {
    const {isLoading} = this.props;
    const filteredSampleDetailsByTableName: SampleDetailsByTableName = {};
    if (!isLoading) {
      for (const [tableName, sampleDetails] of Object.entries(this.props.sampleDetailsByTableName)) {
        filteredSampleDetailsByTableName[tableName] = sampleDetails.filter(({stageRunSample}) =>
          this._isSampleContainable(stageRunSample)
        );
      }
    }
    return this.props.children(filteredSampleDetailsByTableName, isLoading);
  }
}

export default connect(mapStateToProps)(TransferTableNonContainableEntityFilter);
